/**
 * Menu-link catalog source of truth: Credora routes (credoraRouteRegistry) + sidebar links (Menu.jsx).
 * Used for RBAC menu-link assignments and to sync the API catalog.
 */
import { MENUITEMS, LAYOUT_SLUGS } from "../Layout/Sidebar/Menu";
import { credoraAppRoutes } from "../Route/credoraRouteRegistry";
import { CREDORA_PATH_PERMISSIONS } from "./menuAccess";

const pub = process.env.PUBLIC_URL || "";

/** @param {string} path */
export function pathToCredoraRouteKey(path) {
  if (!path || typeof path !== "string") {
    return "";
  }
  let normalized = path;
  if (pub && normalized.startsWith(pub)) {
    normalized = normalized.slice(pub.length);
  }
  normalized = normalized.replace(/^\//, "");
  if (!normalized.startsWith("credora/")) {
    return "";
  }
  const parts = normalized
    .slice("credora/".length)
    .split("/")
    .filter(Boolean)
    .filter((seg) => !seg.startsWith(":") && !LAYOUT_SLUGS.includes(seg));
  return parts.join("/");
}

/** @param {string} path */
function humanizePathKey(pathKey) {
  return pathKey
    .split("/")
    .map((part) => part.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()))
    .join(" / ");
}

/**
 * Walk sidebar MENUITEMS for labels and sections.
 * @returns {Map<string, { title: string, section: string | null, inMenu: boolean }>}
 */
function collectMenuLinkMeta() {
  /** @type {Map<string, { title: string, section: string | null, inMenu: boolean }>} */
  const meta = new Map();
  for (const group of MENUITEMS) {
    for (const section of group.Items || []) {
      const sectionTitle = section.title || null;
      const visit = (item) => {
        if (item.path) {
          const pathKey = pathToCredoraRouteKey(item.path);
          if (pathKey) {
            meta.set(pathKey, {
              title: item.title || humanizePathKey(pathKey),
              section: sectionTitle,
              inMenu: true,
            });
          }
        }
        (item.children || []).forEach(visit);
      };
      if (section.children?.length) {
        section.children.forEach(visit);
      } else {
        visit(section);
      }
    }
  }
  return meta;
}

/** Routes from credoraAppRoutes (includes pages not in sidebar). */
function collectRoutePathKeys() {
  const keys = new Set();
  for (const route of credoraAppRoutes) {
    const pathKey = pathToCredoraRouteKey(route.path);
    if (!pathKey) {
      continue;
    }
    // Skip legacy redirect-only aliases (no dedicated page).
    if (
      pathKey === "loans/active" ||
      pathKey === "loans/schedule" ||
      pathKey === "loans/overdue" ||
      pathKey === "loans/restructured" ||
      pathKey === "integrations/smsgetways" ||
      pathKey === "repayments/failed"
    ) {
      continue;
    }
    keys.add(pathKey);
  }
  return keys;
}

/**
 * @returns {Array<{ pathKey: string, title: string, section: string | null, inMenu: boolean, inRoutes: boolean, requiredPermissions: string[] }>}
 */
export function buildCredoraMenuCatalog() {
  const menuMeta = collectMenuLinkMeta();
  const routeKeys = collectRoutePathKeys();

  /** @type {Map<string, object>} */
  const merged = new Map();

  for (const pathKey of routeKeys) {
    const menu = menuMeta.get(pathKey);
    merged.set(pathKey, {
      pathKey,
      title: menu?.title || humanizePathKey(pathKey),
      section: menu?.section ?? null,
      inMenu: Boolean(menu?.inMenu),
      inRoutes: true,
      requiredPermissions: CREDORA_PATH_PERMISSIONS[pathKey] ?? [],
    });
  }

  for (const [pathKey, menu] of menuMeta.entries()) {
    if (merged.has(pathKey)) {
      const row = merged.get(pathKey);
      row.title = menu.title;
      row.section = menu.section;
      row.inMenu = true;
      continue;
    }
    merged.set(pathKey, {
      pathKey,
      title: menu.title,
      section: menu.section,
      inMenu: true,
      inRoutes: false,
      requiredPermissions: CREDORA_PATH_PERMISSIONS[pathKey] ?? [],
    });
  }

  return [...merged.values()].sort((a, b) => {
    const sec = String(a.section || "").localeCompare(String(b.section || ""));
    if (sec !== 0) {
      return sec;
    }
    return a.pathKey.localeCompare(b.pathKey);
  });
}

let cachedCatalog = null;

export function getCredoraMenuCatalog() {
  if (!cachedCatalog) {
    cachedCatalog = buildCredoraMenuCatalog();
  }
  return cachedCatalog;
}

export function getCredoraMenuCatalogPathKeys() {
  return new Set(getCredoraMenuCatalog().map((item) => item.pathKey));
}

/** Bust cache when hot-reloading in dev. */
export function refreshCredoraMenuCatalog() {
  cachedCatalog = null;
  return getCredoraMenuCatalog();
}
